/* Admin Dashboard — global, UNSCOPED view of the entire database (all
 * locations), plus the deletion-alert history log. Admin-only tab. */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  Building2,
  Share2,
  Loader2,
  Activity,
  Home,
  Trash2,
  AlertTriangle,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import EnrichmentRoiCard from "@/components/EnrichmentRoiCard";
import CardRoiCard from "@/components/CardRoiCard";
import RuralCommunitiesCard from "@/components/RuralCommunitiesCard";
import BillingDashboardCards from "@/components/billing/admin/BillingDashboardCards";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, h] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/deletion-alerts/history"),
        ]);
        setStats(s.data);
        setHistory(h.data);
      } catch (e) {
        toast.error("Failed to load admin stats", { description: formatApiError(e) });
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <div className="flex flex-col gap-1">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Admin only
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-display">
          Admin Dashboard
        </h1>
        <p className="text-sm text-slate-600 max-w-2xl">
          Entire database across <strong>all locations</strong> — unaffected by your
          current location view selection.
        </p>
      </div>

      {!stats ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-8">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <StatCard icon={Users} label="Realtors" value={stats.realtors} testid="admin-stat-realtors" />
            <StatCard
              icon={Building2}
              label="Brokerages"
              value={stats.brokerages_approved}
              sub={`${stats.brokerages_candidates} candidates`}
              testid="admin-stat-brokerages"
            />
            <StatCard icon={Share2} label="With socials" value={stats.socials_filled} testid="admin-stat-socials" />
            <StatCard icon={Activity} label="Running jobs" value={stats.running_jobs} testid="admin-stat-jobs" />
            <StatCard icon={Home} label="Open houses" value={stats.open_houses} testid="admin-stat-openhouses" />
            <StatCard icon={Trash2} label="In recycle bin" value={stats.recycle_bin_items} testid="admin-stat-recyclebin" />
          </div>

          {/* Per-location breakdown */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-400" />
              <h2 className="text-base md:text-lg font-semibold text-slate-900">Per-location breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-per-location-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="px-4 py-2">Location</th>
                    <th className="px-4 py-2 text-right">Realtors</th>
                    <th className="px-4 py-2 text-right">Brokerages</th>
                    <th className="px-4 py-2 text-right">With socials</th>
                    <th className="px-4 py-2 text-right">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.per_location || []).map((loc) => (
                    <tr key={loc.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-900">{loc.name}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{loc.realtors}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{loc.brokerages}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{loc.socials_filled}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{loc.users}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Billing — MRR, member statuses, renewals, GST tracker, acquisition spend */}
      <BillingDashboardCards />

      {/* Rural communities — realtor coverage by town/tier */}
      <RuralCommunitiesCard />

      {/* Enrichment ROI */}
      <EnrichmentRoiCard />

      {/* Digital business card ROI — Linktree, Blinq, Popl breakdown */}
      <CardRoiCard />

      {/* Deletion alert history */}
      <div className="rounded-lg border border-slate-200 bg-white" data-testid="deletion-alert-history">
        <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Deletion alert history</h2>
          <span className="text-xs text-slate-500">
            days where a user deleted 10+ contacts · last {history?.window_days ?? 90} days
          </span>
          <Link to="/recycle-bin" className="ml-auto">
            <Button variant="outline" size="sm" data-testid="history-open-recycle-bin">
              Master Recycle Bin
            </Button>
          </Link>
        </div>
        <div className="p-4 space-y-4">
          {history && history.repeat_offenders.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="repeat-offenders">
              {history.repeat_offenders.map((o) => (
                <span
                  key={o.email}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                    o.days_flagged > 1
                      ? "border-red-300 bg-red-50 text-red-800"
                      : "border-amber-300 bg-amber-50 text-amber-800"
                  }`}
                >
                  {o.email} · flagged {o.days_flagged} day{o.days_flagged > 1 ? "s" : ""} · peak {o.max_count}
                </span>
              ))}
            </div>
          ) : null}
          {!history || history.entries.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="history-empty">
              No deletion alerts recorded yet. An entry is logged whenever any user
              deletes 10+ contacts within 24 hours.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {history.entries.map((e) => (
                <li
                  key={`${e.email}-${e.date}`}
                  className="flex flex-wrap items-center gap-2 py-2 text-sm"
                  data-testid={`history-entry-${e.date}-${e.email}`}
                >
                  <span className="font-mono text-xs text-slate-500 w-24 shrink-0">{e.date}</span>
                  <span className="font-medium text-slate-900">{e.email}</span>
                  <span className="ml-auto rounded-full bg-amber-100 text-amber-900 px-2.5 py-0.5 text-xs font-semibold tabular-nums">
                    {e.count} deletions
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-400">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
      {sub ? <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div> : null}
    </div>
  );
}
